import type { Dimension, NearestPoint, RotorFilterUnit } from '@model/abstract';
import type { ExtremaTarget } from '@type/extrema';
import type {
  CandlestickPoint,
  CandlestickSelector,
  CandlestickTrend,
  MaidrLayer,
} from '@type/grammar';
import type { Movable, MovableDirection } from '@type/movable';
import type { XValue } from '@type/navigation';
import type { AudioState, BrailleState, DescriptionState, TextState, TraceState } from '@type/state';
import { AbstractTrace } from '@model/abstract';
import { NavigationService } from '@service/navigation';
import { Orientation } from '@type/grammar';
import { MathUtil } from '@util/math';
import { Svg } from '@util/svg';
import { MovableGrid } from './movable';

/**
 * Type alias for highlight elements - can be single elements or arrays of elements
 */
type HighlightValue = SVGElement | SVGElement[];

const TREND = 'trend';
const VOLATILITY_PRECISION_MULTIPLIER = 100;

/** Rotor unit that walks only bullish (close > open) candles. */
export const BULLISH_POINT_MODE = 'BULLISH POINT NAVIGATION';
/** Rotor unit that walks only bearish (close < open) candles. */
export const BEARISH_POINT_MODE = 'BEARISH POINT NAVIGATION';
/** Rotor unit that walks only neutral (close === open) candles. */
export const NEUTRAL_POINT_MODE = 'NEUTRAL POINT NAVIGATION';

/**
 * Trend-filter rotor units for the candlestick trace, in cycle order. The
 * `key` is the {@link CandlestickTrend} each unit navigates; the default
 * "all data point" unit is the built-in data mode and is not listed here.
 */
const TREND_ROTOR_UNITS: readonly (RotorFilterUnit & { key: CandlestickTrend })[] = [
  { key: 'Bull', label: BULLISH_POINT_MODE, noun: 'bullish point' },
  { key: 'Bear', label: BEARISH_POINT_MODE, noun: 'bearish point' },
  { key: 'Neutral', label: NEUTRAL_POINT_MODE, noun: 'neutral point' },
];

/**
 * Segment types for candlestick data (open, high, low, close)
 */
type CandlestickSegmentType = 'open' | 'high' | 'low' | 'close';
const SECTIONS = ['volatility', 'open', 'high', 'low', 'close'] as const;

/**
 * Navigation sections of a candlestick trace, in row order (vertical layout).
 * Exported so live-data tooling can target a specific section (e.g. 'close')
 * when announcing newly streamed candles.
 */
export const CANDLESTICK_SECTIONS = SECTIONS;

type CandlestickNavSegmentType = 'volatility' | CandlestickSegmentType;

export class Candlestick extends AbstractTrace {
  protected readonly supportsExtrema = true;
  protected readonly movable: Movable;

  private readonly candles: CandlestickPoint[];
  private readonly candleValues: number[][];

  private readonly orientation: Orientation;
  // Track navigation state separately from visual highlighting state
  private currentSegmentType: CandlestickNavSegmentType | null = 'open';
  private currentPointIndex: number = 0;

  // Performance optimization: Pre-computed lookup tables
  private readonly sortedSegmentsByPoint: CandlestickNavSegmentType[][];
  private readonly segmentPositionMaps: Map<
    CandlestickNavSegmentType,
    number
  >[];

  private readonly sections: typeof SECTIONS;

  private readonly min: number;
  private readonly max: number;

  // Pre-computed braille data. Candles are immutable after construction, so
  // per-row min/max and trends are computed once here instead of on every
  // state emission (every keypress / autoplay tick).
  private readonly perRowMin: number[];
  private readonly perRowMax: number[];
  private readonly trends: CandlestickTrend[];

  // Rotor trend-filter units present in the data, computed once. Candles are
  // immutable after construction, so this avoids rebuilding the present-trend
  // Set on every getRotorFilterUnits() call (twice per keystroke via the
  // rotor service).
  private readonly rotorFilterUnits: readonly RotorFilterUnit[];

  protected readonly highlightValues: HighlightValue[][] | null;
  protected highlightCenters:
    | { x: number; y: number; row: number; col: number; element: SVGElement }[]
    | null;

  // Service dependency for navigation logic
  protected override readonly navigationService: NavigationService;

  /**
   * Creates a new Candlestick instance from a MAIDR layer
   * @param layer - The MAIDR layer containing candlestick data
   */
  constructor(layer: MaidrLayer) {
    super(layer);

    // Initialize navigation service
    this.navigationService = new NavigationService();

    const data = layer.data as CandlestickPoint[];
    this.candles = data.map(candle => ({
      ...candle,
      volatility:
        Math.round(
          (candle.high - candle.low) * VOLATILITY_PRECISION_MULTIPLIER,
        ) / VOLATILITY_PRECISION_MULTIPLIER,
      trend:
        candle.close > candle.open
          ? 'Bull'
          : candle.close < candle.open
            ? 'Bear'
            : 'Neutral',
    }));

    this.orientation = layer.orientation ?? Orientation.VERTICAL;
    this.sections = SECTIONS;

    this.candleValues = this.sections.map(key =>
      this.candles.map(c => c[key]),
    );
    const options = this.orientation === Orientation.HORIZONTAL
      ? { col: this.sections.length - 1 }
      : { row: this.sections.length - 1 };
    this.movable = new MovableGrid<number>(this.candleValues, options);

    this.min = MathUtil.minFrom2D(this.candleValues);
    this.max = MathUtil.maxFrom2D(this.candleValues);

    // Pre-compute per-row min/max and trends once; the braille getter returns
    // these cached references (see get braille) rather than rebuilding them
    // per keypress. safeMin/safeMax avoid the spread-argument RangeError that
    // Math.min(...row) hits on very large rows.
    this.perRowMin = this.candleValues.map(row => MathUtil.safeMin(row));
    this.perRowMax = this.candleValues.map(row => MathUtil.safeMax(row));
    this.trends = this.candles.map(candle => candle.trend);

    const presentTrends = new Set<CandlestickTrend>(this.trends);
    this.rotorFilterUnits = TREND_ROTOR_UNITS.filter(unit =>
      presentTrends.has(unit.key),
    );

    // Pre-compute sorted segments and position maps for performance
    this.sortedSegmentsByPoint = this.precomputeSortedSegments();
    this.segmentPositionMaps = this.precomputePositionMaps();

    // Initialize navigation state
    this.currentPointIndex = 0;
    this.currentSegmentType = 'close';

    // Initialize visual positioning for highlighting (keep original structure)
    if (this.orientation === Orientation.HORIZONTAL) {
      this.col = 0; // Points to 'open' segment index in sections array
    } else {
      this.row = 0; // Points to 'open' segment index in sections array
    }

    this.highlightValues = this.mapToSvgElements(
      layer.selectors as string | string[] | CandlestickSelector | undefined,
    );
    this.highlightCenters = this.mapSvgElementsToCenters();
  }

  /**
   * Pre-computes sorted segments for all candlestick points for O(1) lookup
   * @returns Array of sorted segment types for each candlestick point
   */
  private precomputeSortedSegments(): CandlestickNavSegmentType[][] {
    return this.candles.map((candle) => {
      // Always put 'volatility' first, then sort OHLC by value ascending
      const ohlcSegments: CandlestickSegmentType[] = [
        'low',
        'open',
        'close',
        'high',
      ];
      const sortedOhlc = ohlcSegments
        .map(seg => [seg, candle[seg]] as [CandlestickSegmentType, number])
        .sort((a, b) => a[1] - b[1])
        .map(pair => pair[0]);
      return ['volatility', ...sortedOhlc];
    });
  }

  /**
   * Pre-computes position maps for O(1) lookup of segment positions
   * @returns Array of position maps for each candlestick point
   */
  private precomputePositionMaps(): Map<CandlestickNavSegmentType, number>[] {
    return this.sortedSegmentsByPoint.map((sortedSegments) => {
      const positionMap = new Map<CandlestickNavSegmentType, number>();
      sortedSegments.forEach((segmentType, index) => {
        positionMap.set(segmentType, index);
      });
      return positionMap;
    });
  }

  /**
   * Gets the position index of a segment type in the sorted segments for a point (O(1) lookup)
   * @param pointIndex - Index of the candlestick point
   * @param segmentType - Type of segment to find
   * @returns Position index of the segment in sorted order
   */
  private getSegmentPositionInSortedOrder(
    pointIndex: number,
    segmentType: CandlestickNavSegmentType,
  ): number {
    return this.segmentPositionMaps[pointIndex].get(segmentType) ?? 0;
  }

  /**
   * Gets the segment type at a specific position in the sorted order for a point (O(1) lookup)
   * @param pointIndex - Index of the candlestick point
   * @param position - Position in the sorted order
   * @returns Segment type at the specified position
   */
  private getSegmentTypeAtSortedPosition(
    pointIndex: number,
    position: number,
  ): CandlestickNavSegmentType {
    const sortedSegments = this.sortedSegmentsByPoint[pointIndex];
    return sortedSegments[position] ?? 'open';
  }

  /**
   * Updates visual position for segment highlighting using dynamic value-sorted order
   */
  private updateVisualSegmentPosition(): void {
    // Use the sorted navigation order (with volatility first)
    const navOrder = this.sortedSegmentsByPoint[this.currentPointIndex];
    const dynamicSegmentPosition = navOrder.indexOf(
      this.currentSegmentType ?? 'open',
    );
    if (this.orientation === Orientation.HORIZONTAL) {
      this.col = dynamicSegmentPosition;
    } else {
      this.row = dynamicSegmentPosition;
    }
  }

  /**
   * Updates visual position for point highlighting and segment position
   */
  protected override updateVisualPointPosition(): void {
    if (this.orientation === Orientation.HORIZONTAL) {
      this.row = this.currentPointIndex;
    } else {
      this.col = this.currentPointIndex;
    }

    // Also update segment position since candlestick needs both
    this.updateVisualSegmentPosition();
  }

  /**
   * Handles initial entry into the candlestick chart, setting default position
   */
  protected handleInitialEntry(): void {
    this.isInitialEntry = false;
    this.currentPointIndex = Math.max(
      0,
      Math.min(this.currentPointIndex, this.candles.length - 1),
    );
    // Always start at 'close' on initial entry
    this.currentSegmentType = 'close';
    this.updateVisualSegmentPosition();
  }

  /**
   * Moves navigation position one step in the specified direction
   * @param direction - Direction to move (UPWARD, DOWNWARD, FORWARD, BACKWARD)
   */
  public override moveOnce(direction: MovableDirection): boolean {
    if (this.isInitialEntry) {
      this.handleInitialEntry();
      this.notifyStateUpdate();
      return true;
    }

    if (!this.isMovable(direction)) {
      this.notifyOutOfBounds();
      return false;
    }

    switch (direction) {
      case 'UPWARD':
      case 'DOWNWARD': {
        // Vertical movement: navigate between segments within the same candlestick (value-sorted)
        const navOrder = this.sortedSegmentsByPoint[this.currentPointIndex];
        const currentSegmentPosition = navOrder.indexOf(
          this.currentSegmentType ?? 'open',
        );
        if (currentSegmentPosition === -1) {
          this.notifyOutOfBounds();
          return false;
        }
        const newSegmentPosition
          = direction === 'UPWARD'
            ? currentSegmentPosition + 1
            : currentSegmentPosition - 1;
        if (newSegmentPosition >= 0 && newSegmentPosition < navOrder.length) {
          this.currentSegmentType = navOrder[newSegmentPosition];
          this.updateVisualSegmentPosition();
        } else {
          this.notifyOutOfBounds();
          return false;
        }
        break;
      }

      case 'FORWARD':
      case 'BACKWARD': {
        // Horizontal movement: navigate between candlesticks while preserving segment type
        const newPointIndex
          = direction === 'FORWARD'
            ? this.currentPointIndex + 1
            : this.currentPointIndex - 1;

        if (newPointIndex >= 0 && newPointIndex < this.candles.length) {
          this.currentPointIndex = newPointIndex;
          this.updateVisualPointPosition();
          this.updateVisualSegmentPosition();
        } else {
          this.notifyOutOfBounds();
          return false;
        }
        break;
      }
    }

    this.notifyStateUpdate();
    return true;
  }

  public override moveToExtreme(direction: MovableDirection): boolean {
    if (this.isInitialEntry) {
      this.handleInitialEntry();
    }

    switch (direction) {
      case 'UPWARD': {
        // Move to the highest value segment in current candlestick
        const currentSorted
          = this.sortedSegmentsByPoint[this.currentPointIndex];
        this.currentSegmentType = currentSorted[currentSorted.length - 1];
        this.updateVisualSegmentPosition();
        break;
      }
      case 'DOWNWARD': {
        // Move to the lowest value segment in current candlestick
        const currentSortedDown
          = this.sortedSegmentsByPoint[this.currentPointIndex];
        this.currentSegmentType = currentSortedDown[0];
        this.updateVisualSegmentPosition();
        break;
      }
      case 'FORWARD': {
        // Move to the last candlestick (rightmost)
        this.currentPointIndex = this.candles.length - 1;
        this.updateVisualPointPosition();
        this.updateVisualSegmentPosition();
        break;
      }
      case 'BACKWARD': {
        // Move to the first candlestick (leftmost)
        this.currentPointIndex = 0;
        this.updateVisualPointPosition();
        this.updateVisualSegmentPosition();
        break;
      }
    }

    this.notifyStateUpdate();
    return true;
  }

  /**
   * Computes the state at an arbitrary position without moving the cursor.
   *
   * Candlestick state getters read `currentPointIndex`/`currentSegmentType`
   * rather than `row`/`col`, so those are mapped from the requested position
   * and restored alongside the cursor.
   *
   * @param row - The row of the position to compute state for
   * @param col - The column of the position to compute state for
   * @returns The trace state at the requested position
   */
  public override getStateAt(row: number, col: number): TraceState {
    const previous = {
      pointIndex: this.currentPointIndex,
      segmentType: this.currentSegmentType,
    };
    // Raise the no-notify guard before any pre-super mutation so the whole
    // override is covered, not just the base-class window.
    this.isComputingStateAt = true;
    try {
      const { pointIndex, segmentType }
        = this.navigationService.computeIndexAndSegment(
          row,
          col,
          this.orientation,
          this.sections,
        );
      this.currentPointIndex = pointIndex;
      this.currentSegmentType = segmentType;
      return super.getStateAt(row, col);
    } finally {
      // The base-class finally (inside super.getStateAt) restores
      // row/col/isInitialEntry and lowers the guard; this finally restores
      // the candlestick fields and clears the guard on pre-super throws.
      this.isComputingStateAt = false;
      this.currentPointIndex = previous.pointIndex;
      this.currentSegmentType = previous.segmentType;
    }
  }

  public override moveToIndex(row: number, col: number): boolean {
    // Delegate navigation logic to service and only handle data state updates
    if (this.isInitialEntry) {
      this.handleInitialEntry();
    }

    // Use navigation service to compute the mapping
    const { pointIndex, segmentType }
      = this.navigationService.computeIndexAndSegment(
        row,
        col,
        this.orientation,
        this.sections,
      );

    // Update Core Model state
    this.currentPointIndex = pointIndex;
    this.currentSegmentType = segmentType;
    this.row = row;
    this.col = col;

    this.updateVisualSegmentPosition();
    this.updateVisualPointPosition();
    this.notifyStateUpdate();
    return true;
  }

  /**
   * Checks if movement to the target position or direction is possible
   * @param target - Target position array or movement direction
   * @returns True if movement is possible, false otherwise
   */
  public override isMovable(target: [number, number] | MovableDirection): boolean {
    if (Array.isArray(target)) {
      // For direct position targeting, use parent logic
      return super.isMovable(target);
    }

    if (this.isInitialEntry) {
      return true;
    }

    switch (target) {
      case 'UPWARD':
      case 'DOWNWARD': {
        // Vertical movement: check if we can move between segments within the same candlestick
        const navOrder = this.sortedSegmentsByPoint[this.currentPointIndex];
        const currentSegmentPosition = navOrder.indexOf(
          this.currentSegmentType ?? 'open',
        );
        const newSegmentPosition
          = target === 'UPWARD'
            ? currentSegmentPosition + 1
            : currentSegmentPosition - 1;
        return newSegmentPosition >= 0 && newSegmentPosition < navOrder.length;
      }

      case 'FORWARD':
      case 'BACKWARD': {
        // Horizontal movement: check if we can move between candlesticks
        const newPointIndex
          = target === 'FORWARD'
            ? this.currentPointIndex + 1
            : this.currentPointIndex - 1;
        return newPointIndex >= 0 && newPointIndex < this.candles.length;
      }
    }
  }

  /**
   * Gets the description state for the candlestick trace.
   * @returns The description state containing chart metadata and data table
   */
  public get description(): DescriptionState {
    const bullCount = this.candles.filter(c => c.trend === 'Bull').length;
    const bearCount = this.candles.filter(c => c.trend === 'Bear').length;

    const stats: DescriptionState['stats'] = [
      { label: 'Number of periods', value: this.candles.length },
      { label: 'Price range', value: `${this.min} to ${this.max}` },
      { label: 'Bull count', value: bullCount },
      { label: 'Bear count', value: bearCount },
    ];

    const headers = ['Date', 'Open', 'High', 'Low', 'Close', 'Volume', 'Trend'];
    const rows: (string | number)[][] = this.candles.map(c => [
      c.value,
      c.open,
      c.high,
      c.low,
      c.close,
      c.volume ?? '',
      c.trend,
    ]);

    return {
      chartType: this.getChartTypeLabel(),
      title: this.title,
      axes: this.getDescriptionAxes(),
      stats,
      dataTable: { headers, rows },
    };
  }

  /**
   * Cleans up resources and disposes of the candlestick instance
   */
  public override dispose(): void {
    this.navigationService.dispose();
    this.candles.length = 0;
    super.dispose();
  }

  /**
   * Gets the 2D array of candlestick values
   * @returns Array of candlestick values for all segments
   */
  protected get values(): number[][] {
    return this.candleValues;
  }

  protected get audio(): AudioState {
    let value: number;
    const isHorizontal = this.orientation === Orientation.HORIZONTAL;
    const candleCount = this.candles.length;
    const sectionCount = this.candleValues.length;
    if (this.currentSegmentType === 'volatility') {
      value = this.candles[this.currentPointIndex].volatility;
    } else if (this.currentSegmentType) {
      value = this.candles[this.currentPointIndex][this.currentSegmentType];
    } else {
      value = this.candles[this.currentPointIndex].open;
    }

    return {
      freq: {
        min: this.min,
        max: this.max,
        raw: value,
      },
      panning: {
        // x is the candle index in both orientations (this.col vertical,
        // this.row horizontal); rows/cols describe the grid shape
        // (sections x candles) independent of orientation, so panning pans by
        // candle position regardless of how many candles the chart holds.
        x: isHorizontal ? this.row : this.col,
        y: isHorizontal ? this.col : this.row,
        rows: sectionCount,
        cols: candleCount,
      },
      trend: this.candles[this.currentPointIndex].trend,
    };
  }

  protected get braille(): BrailleState {
    // Braille cells index into candleValues, which is in static SECTIONS
    // order ['volatility','open','high','low','close']. The braille row must
    // therefore be the static section index of the current segment (not its
    // value-sorted navigation position), and the col is the candle index.
    //
    // This getter must stay side-effect free: it runs on every state emission
    // (AbstractTrace.state) and during getStateAt, so mutating this.row/col
    // here would corrupt the highlight computed alongside it (and, for
    // horizontal charts, clobber the candle index this.row holds). All heavy
    // data (per-row min/max, trends) is precomputed in the constructor.
    return {
      empty: false,
      id: this.id,
      values: this.candleValues, // includes volatility and OHLC
      min: this.perRowMin,
      max: this.perRowMax,
      row: this.sections.indexOf(this.currentSegmentType ?? this.sections[0]),
      col: this.currentPointIndex,
      custom: this.trends,
    };
  }

  /**
   * Collects SVG elements matching the provided selectors
   * @param selector - CSS selector(s) to match elements
   * @returns Array of matched SVG elements
   */
  private collectElements(selector?: string | string[]): SVGElement[] {
    if (!selector)
      return [];
    const selectorArray = Array.isArray(selector) ? selector : [selector];
    const elements: SVGElement[] = [];
    for (const sel of selectorArray) {
      elements.push(...Svg.selectAllElements(sel));
    }
    return elements;
  }

  /**
   * Gets an element at the specified index from an array, or null if out of bounds
   * @param array - Array of SVG elements
   * @param index - Index to retrieve
   * @returns SVG element at index or null
   */
  private getElementAt(array: SVGElement[], index: number): SVGElement | null {
    return index < array.length ? array[index] : null;
  }

  /**
   * Maps candlestick selectors to 2D array of SVG elements for highlighting
   * @param selectors - CSS selectors or structured candlestick selectors
   * @returns 2D array of SVG elements or null if no selectors provided
   */
  protected mapToSvgElements(
    selectors: string | string[] | CandlestickSelector | undefined,
  ): HighlightValue[][] | null {
    if (!selectors) {
      return null;
    }

    // Legacy: a single selector for all elements
    if (typeof selectors === 'string' || Array.isArray(selectors)) {
      const selectorString = Array.isArray(selectors)
        ? selectors[0] || ''
        : selectors;
      const allElements = Svg.selectAllElements(selectorString);

      const segmentElements: HighlightValue[][] = [];
      for (let pos = 0; pos < this.sections.length; pos++) {
        segmentElements[pos] = [];
        for (
          let pointIndex = 0;
          pointIndex < this.candles.length;
          pointIndex++
        ) {
          const elementIndex = pointIndex < allElements.length ? pointIndex : 0;
          segmentElements[pos][pointIndex] = allElements[elementIndex];
        }
      }
      return segmentElements;
    }

    // Structured selectors
    const cs = selectors as CandlestickSelector;
    const N = this.candles.length;

    const bodies = this.collectElements(cs.body);
    let highs = this.collectElements(cs.wickHigh);
    let lows = this.collectElements(cs.wickLow);
    // Fallback to a single combined wick if provided
    if (highs.length === 0 || lows.length === 0) {
      const combined = this.collectElements(cs.wick);
      if (combined.length > 0) {
        if (highs.length === 0)
          highs = combined;
        if (lows.length === 0)
          lows = combined;
      }
    }
    const opens = this.collectElements(cs.open);
    const closes = this.collectElements(cs.close);
    // Volatility will be composed from [wickHigh, body, wickLow]; no direct selectors used

    const derivedOpen: SVGElement[] = Array.from({ length: N }, () =>
      Svg.createEmptyElement());
    const derivedClose: SVGElement[] = Array.from({ length: N }, () =>
      Svg.createEmptyElement());
    const derivedVolatility: SVGElement[] = Array.from({ length: N }, () =>
      Svg.createEmptyElement());

    for (let i = 0; i < N; i++) {
      // Open (explicit otherwise derive from body using data)
      let openEl = this.getElementAt(opens, i);
      if (!openEl) {
        const body = this.getElementAt(bodies, i);
        if (body) {
          const { open, close } = this.candles[i];
          const edge: 'top' | 'bottom'
            = close > open ? 'bottom' : close < open ? 'top' : 'bottom';
          openEl = Svg.createLineElement(body, edge);
        } else {
          openEl = Svg.createEmptyElement();
        }
      }
      derivedOpen[i] = openEl;

      // Close (explicit otherwise derive from body using data)
      let closeEl = this.getElementAt(closes, i);
      if (!closeEl) {
        const body = this.getElementAt(bodies, i);
        if (body) {
          const { open, close } = this.candles[i];
          const edge: 'top' | 'bottom'
            = close > open ? 'top' : close < open ? 'bottom' : 'top';
          closeEl = Svg.createLineElement(body, edge);
        } else {
          closeEl = Svg.createEmptyElement();
        }
      }
      derivedClose[i] = closeEl;

      // Volatility: composed later as [high, body, low]; no single element here
      derivedVolatility[i] = Svg.createEmptyElement();
    }

    // Build 2D array in value-sorted navigation order per point
    const segmentElements: HighlightValue[][] = Array.from(
      { length: this.sections.length },
      () => Array.from({ length: N }, () => Svg.createEmptyElement()),
    );

    for (let pointIndex = 0; pointIndex < N; pointIndex++) {
      const navOrder = this.sortedSegmentsByPoint[pointIndex]; // ['volatility', ...sorted OHLC]
      for (let pos = 0; pos < navOrder.length; pos++) {
        const seg = navOrder[pos];
        let el: HighlightValue;
        switch (seg) {
          case 'volatility':
            {
              const parts: SVGElement[] = [];
              const body = this.getElementAt(bodies, pointIndex);
              const hi = this.getElementAt(highs, pointIndex) ?? body;
              const lo = this.getElementAt(lows, pointIndex) ?? body;
              if (hi)
                parts.push(hi);
              if (body)
                parts.push(body);
              if (lo)
                parts.push(lo);
              const unique = Array.from(new Set(parts));
              el = unique.length > 0 ? unique : [Svg.createEmptyElement()];
            }
            break;
          case 'open':
            el = derivedOpen[pointIndex];
            break;
          case 'close':
            el = derivedClose[pointIndex];
            break;
          case 'high':
            el
              = this.getElementAt(highs, pointIndex)
                ?? this.getElementAt(bodies, pointIndex)
                ?? Svg.createEmptyElement();
            break;
          case 'low':
            el
              = this.getElementAt(lows, pointIndex)
                ?? this.getElementAt(bodies, pointIndex)
                ?? Svg.createEmptyElement();
            break;
          default:
            el = Svg.createEmptyElement();
        }
        segmentElements[pos][pointIndex] = el;
      }
    }

    return segmentElements;
  }

  protected get text(): TextState {
    const point = this.candles[this.currentPointIndex];
    const isHorizontal = this.orientation === Orientation.HORIZONTAL;
    let crossValue: number;
    if (this.currentSegmentType === 'volatility') {
      crossValue = point.volatility;
    } else if (this.currentSegmentType) {
      crossValue = point[this.currentSegmentType];
    } else {
      crossValue = point.open;
    }

    return {
      main: {
        label: isHorizontal ? this.yAxis : this.xAxis,
        value: point.value,
      },
      cross: {
        label: isHorizontal ? this.xAxis : this.yAxis,
        value: crossValue,
      },
      section: this.currentSegmentType ?? 'open',
      z: { label: TREND, value: point.trend },
      mainAxis: isHorizontal ? 'y' : 'x',
      crossAxis: isHorizontal ? 'x' : 'y',
    };
  }

  /**
   * Gets the current candlestick trend for audio palette selection
   * @returns The trend of the current candlestick point
   */
  public getCurrentTrend(): CandlestickTrend {
    return this.candles[this.currentPointIndex].trend;
  }

  /**
   * Gets the candle data points. Used by the candlestick delta feature to
   * derive a virtual comparison layer against a reference line.
   * @returns The candle points in x order
   */
  public getCandles(): readonly CandlestickPoint[] {
    return this.candles;
  }

  /**
   * Gets the current X value from the candlestick trace
   * @returns The current X value or null if not available
   */
  public override getCurrentXValue(): XValue | null {
    if (
      this.currentPointIndex >= 0
      && this.currentPointIndex < this.candles.length
    ) {
      return this.candles[this.currentPointIndex].value;
    }
    return null;
  }

  /**
   * Moves the candlestick to the position that matches the given X value
   * @param xValue - The X value to move to
   * @returns True if the position was found and set, false otherwise
   */
  public override moveToXValue(xValue: XValue): boolean {
    const targetIndex = this.candles.findIndex(
      candle => candle.value === xValue,
    );
    if (targetIndex !== -1) {
      this.currentPointIndex = targetIndex;
      this.currentSegmentType = 'close'; // Default to close segment
      this.updateVisualPointPosition();
      this.updateVisualSegmentPosition();
      this.notifyStateUpdate();
      return true;
    }
    return false;
  }

  /**
   * Gets available X values for navigation
   * @returns Array of X values
   */
  public getAvailableXValues(): XValue[] {
    return this.candles.map(candle => candle.value);
  }

  /**
   * Gets extrema targets for the current candlestick trace with labels and descriptions
   * @returns Array of extrema targets for navigation
   */
  public override getExtremaTargets(): ExtremaTarget[] {
    const targets: ExtremaTarget[] = [];
    const currentSegment = this.currentSegmentType ?? 'open';

    // Only add extrema for the current segment
    if (currentSegment === 'volatility') {
      // For volatility, find all max and min values (there could be multiple)
      const volatilityValues = this.candles.map((c, index) => ({
        value: c.volatility,
        index,
      }));
      // Find all maximum values (there could be multiple candles with the same max volatility)
      const maxVolatility = Math.max(...volatilityValues.map(v => v.value));
      const maxVolatilityIndices = volatilityValues
        .filter(v => v.value === maxVolatility)
        .map(v => v.index);

      // Find all minimum values
      const minVolatility = Math.min(...volatilityValues.map(v => v.value));
      const minVolatilityIndices = volatilityValues
        .filter(v => v.value === minVolatility)
        .map(v => v.index);

      // Add all max volatility targets
      maxVolatilityIndices.forEach((index, _count) => {
        const candle = this.candles[index];
        targets.push({
          label: `Max Volatility at ${candle.value}`,
          value: candle.volatility,
          pointIndex: index,
          segment: 'volatility',
          type: 'max',
          navigationType: 'point',
          xValue: candle.value,
        });
      });

      // Add all min volatility targets
      minVolatilityIndices.forEach((index, _count) => {
        const candle = this.candles[index];
        targets.push({
          label: `Min Volatility at ${candle.value}`,
          value: candle.volatility,
          pointIndex: index,
          segment: 'volatility',
          type: 'min',
          navigationType: 'point',
          xValue: candle.value,
        });
      });
    } else {
      // For OHLC segments, find all min and max values
      const segmentValues = this.candles.map((c, index) => ({
        value: c[currentSegment],
        index,
        xValue: c.value,
      }));
      // Find all maximum values
      const maxValue = Math.max(...segmentValues.map(v => v.value));
      const maxIndices = segmentValues
        .filter(v => v.value === maxValue)
        .map(v => v.index);

      // Find all minimum values
      const minValue = Math.min(...segmentValues.map(v => v.value));
      const minIndices = segmentValues
        .filter(v => v.value === minValue)
        .map(v => v.index);

      // Add all max targets
      maxIndices.forEach((index, _count) => {
        const candle = this.candles[index];
        const segmentLabel
          = currentSegment.charAt(0).toUpperCase() + currentSegment.slice(1);
        targets.push({
          label: `Max ${segmentLabel} at ${candle.value}`,
          value: candle[currentSegment],
          pointIndex: index,
          segment: currentSegment,
          type: 'max',
          navigationType: 'point',
          xValue: candle.value,
        });
      });

      // Add all min targets
      minIndices.forEach((index, _count) => {
        const candle = this.candles[index];
        const segmentLabel
          = currentSegment.charAt(0).toUpperCase() + currentSegment.slice(1);
        targets.push({
          label: `Min ${segmentLabel} at ${candle.value}`,
          value: candle[currentSegment],
          pointIndex: index,
          segment: currentSegment,
          type: 'min',
          navigationType: 'point',
          xValue: candle.value,
        });
      });
    }

    return targets;
  }

  /**
   * Navigates to a specific extrema target
   * @param target - The extrema target to navigate to
   */
  public override navigateToExtrema(target: ExtremaTarget): void {
    // Update the current point index
    this.currentPointIndex = target.pointIndex;

    // Update the current segment type
    this.currentSegmentType = target.segment as CandlestickNavSegmentType;

    // Use common finalization method
    this.finalizeNavigation();
  }

  /**
   * Moves to the next value in the specified direction that matches the comparison type
   * @param direction - Direction to search (left or right)
   * @param type - Comparison type (lower or higher)
   * @returns True if a matching value was found and moved to
   */
  public override moveToNextCompareValue(direction: 'left' | 'right', type: 'lower' | 'higher'): boolean {
    // Establish the entry position on the first move so the compare jump
    // highlights and a subsequent ordinary keypress isn't swallowed by the
    // initial-entry branch of moveOnce (mirrors moveOnce/moveToExtreme).
    if (this.isInitialEntry) {
      this.handleInitialEntry();
    }

    // Drive off currentPointIndex, which is the candle index in BOTH
    // orientations. Reading this.col directly is only correct in the vertical
    // layout — in the horizontal layout this.col holds the segment position
    // and this.row holds the candle index (see updateVisualPointPosition).
    // updateVisualPointPosition() maps currentPointIndex back to row/col.
    const currentIndex = this.currentPointIndex;
    if (currentIndex < 0 || currentIndex >= this.candles.length) {
      return false;
    }
    const currentSegment = this.currentSegmentType ?? 'open';

    const step = direction === 'right' ? 1 : -1;
    for (let i = currentIndex + step; i >= 0 && i < this.candles.length; i += step) {
      if (this.compare(this.candles[i][currentSegment], this.candles[currentIndex][currentSegment], type)) {
        this.currentPointIndex = i;
        this.updateVisualPointPosition();
        this.notifyStateUpdate();
        return true;
      }
    }
    this.notifyRotorBounds();
    return false;
  }

  /**
   * Moves upward between segments within a candle in rotor mode
   * @returns True if the move was successful
   */
  public override moveUpRotor(): boolean {
    this.moveOnce('UPWARD');
    return true;
  }

  /**
   * Moves downward between segments within a candle in rotor mode
   * @returns True if the move was successful
   */
  public override moveDownRotor(): boolean {
    this.moveOnce('DOWNWARD');
    return true;
  }

  /**
   * Exposes the bullish/bearish/neutral rotor filter units. These are
   * appended after the built-in lower/higher value compare units, so cycling
   * the rotor offers: all data point (default), lower value, higher value,
   * and one unit per trend that actually occurs in the data. The default
   * "all data point" unit is provided by the built-in data mode.
   *
   * A trend with no candles is omitted so the rotor cycle carries no
   * dead-end modes (where every move would just report "no point found"),
   * mirroring how GRID_MODE / INTERSECTION_MODE are gated on capability. The
   * list is precomputed in the constructor (candles are immutable), so this
   * returns the cached reference — callers must treat it as read-only.
   * @returns The present trend-filter rotor units in cycle order
   */
  public override getRotorFilterUnits(): readonly RotorFilterUnit[] {
    return this.rotorFilterUnits;
  }

  /**
   * Jumps to the previous/next candle whose trend matches the active filter
   * unit, preserving the current segment. Trend filtering runs along the
   * candle axis; the rotor service handles up/down (announcing them as
   * unavailable) and only dispatches left/right here.
   * @param key - The trend to navigate ('Bull', 'Bear', or 'Neutral')
   * @param direction - The direction to search
   * @returns True if a matching candle was found and moved to
   */
  public override moveToRotorFilter(
    key: string,
    direction: 'left' | 'right',
  ): boolean {
    // Establish the entry position on the first move so this jump highlights
    // and the initial-entry branch of moveOnce doesn't later swallow a
    // keypress (mirrors moveOnce/moveToExtreme/moveToIndex).
    if (this.isInitialEntry) {
      this.handleInitialEntry();
    }

    const step = direction === 'right' ? 1 : -1;
    for (
      let i = this.currentPointIndex + step;
      i >= 0 && i < this.candles.length;
      i += step
    ) {
      if (this.candles[i].trend === key) {
        this.currentPointIndex = i;
        this.updateVisualPointPosition();
        this.notifyStateUpdate();
        return true;
      }
    }

    this.notifyRotorBounds();
    return false;
  }

  /**
   * Maps SVG elements to their center coordinates for click navigation
   * @returns Array of center coordinates with row/col indices or null
   */
  protected mapSvgElementsToCenters():
    | { x: number; y: number; row: number; col: number; element: SVGElement }[]
    | null {
    const svgElements: (SVGElement | SVGElement[])[][] | null = this.highlightValues;

    if (!svgElements) {
      return null;
    }

    const centers: {
      x: number;
      y: number;
      row: number;
      col: number;
      element: SVGElement;
    }[] = [];
    for (let row = 0; row < svgElements.length; row++) {
      for (let col = 0; col < svgElements[row].length; col++) {
        const element = svgElements[row][col];
        const targetElement = Array.isArray(element) ? element[0] : element;
        if (targetElement) {
          const bbox = targetElement.getBoundingClientRect();
          centers.push({
            x: bbox.x + bbox.width / 2,
            y: bbox.y + bbox.height / 2,
            row,
            col,
            element: targetElement,
          });
        }
      }
    }

    return centers;
  }

  /**
   * Finds the nearest candlestick point to the given coordinates
   * @param x - X coordinate
   * @param y - Y coordinate
   * @returns Nearest point information or null
   */
  public findNearestPoint(
    x: number,
    y: number,
  ): NearestPoint | null {
    // loop through highlightCenters to find nearest point
    if (!this.highlightCenters) {
      return null;
    }

    let nearestDistance = Infinity;
    let nearestIndex = -1;

    for (let i = 0; i < this.highlightCenters.length; i++) {
      const center = this.highlightCenters[i];
      const distance = Math.hypot(center.x - x, center.y - y);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = i;
      }
    }

    if (nearestIndex === -1) {
      return null;
    }

    return {
      element: this.highlightCenters[nearestIndex].element,
      row: this.highlightCenters[nearestIndex].row,
      col: this.highlightCenters[nearestIndex].col,
      centerX: this.highlightCenters[nearestIndex].x,
      centerY: this.highlightCenters[nearestIndex].y,
    };
  }

  /**
   * Moves the trace to the nearest highlight point for a pointer/hover event.
   *
   * The highlight grid is [sortedSegmentPosition][pointIndex] in BOTH
   * orientations (see mapToSvgElements), so `nearest.col` is always the candle
   * index and `nearest.row` is the value-sorted segment position. Those sorted
   * coordinates are translated straight into the candle/segment model state
   * here, rather than delegating to moveToIndex, which interprets rows in
   * static CANDLESTICK_SECTIONS order — an interpretation the braille path
   * relies on and must keep.
   *
   * @param _x - Screen-space x position of the pointer
   * @param _y - Screen-space y position of the pointer
   * @param nearest - The nearest highlight point in sorted grid coordinates
   * @param onCurve - Whether the pointer is within the point's bounds
   */
  protected override moveToNearest(
    _x: number,
    _y: number,
    nearest: NearestPoint,
    onCurve: boolean,
  ): void {
    if (!onCurve) {
      return;
    }
    if (this.row === nearest.row && this.col === nearest.col) {
      return;
    }
    const pointIndex = nearest.col;
    this.currentPointIndex = pointIndex;
    this.currentSegmentType = this.getSegmentTypeAtSortedPosition(
      pointIndex,
      nearest.row,
    );
    this.isInitialEntry = false;
    // Syncs both the candle position and the segment position for highlight.
    this.updateVisualPointPosition();
    this.notifyStateUpdate();
  }

  protected get dimension(): Dimension {
    // Orientation-independent: candlestick maps UPWARD/DOWNWARD to the 5
    // sections and FORWARD/BACKWARD to the N candles in BOTH orientations
    // (see moveOnce), and AutoplayState is keyed by direction, so rows must
    // always be the section count and cols the candle count. Conditioning on
    // orientation here mis-paces autoplay and mis-clamps isMovable bounds.
    return {
      rows: this.candleValues.length,
      cols: this.candles.length,
    };
  }
}
